import { useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  GetOrdersReportRequest,
  GetOrdersReportResponse,
  GetOrdersReportSummaryRequest,
  GetOrdersReportSummaryResponse,
} from '@classpass/protobuf-definitions/lib/web/models/partners/sku_order';
import { useQuery } from '@tanstack/react-query';

import { createErrorSnackbar, createSuccessSnackbar } from 'components/Snackbar';
import URLS from 'constants/urls';
import { transformKeysToCamelCase } from 'helpers/core';
import { reportError } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';

import { csvRowCount, downloadCsvResponse } from '../shared/SkuCsvDownload.helpers';

import messages from './SkuOrderReports.messages';
import { normalizeOrdersReport, normalizeOrdersReportSummary } from './SkuOrderReports.normalizers';

export const ORDERS_REPORT_PAGE_SIZE = 20;
export const ORDERS_REPORT_CSV_MAX_ROWS = 50000;

const CSV_DEFAULT_FILE_NAME = 'orders-report.csv';

export const skuOrderReportsKeys = {
  all: ['skuOrderReports'],
  summary: scope => [...skuOrderReportsKeys.all, 'summary', scope],
  orders: (filter, cursor) => [...skuOrderReportsKeys.all, 'orders', filter, cursor],
};

async function fetchOrdersReportSummary(scope) {
  try {
    const response = await httpClient.post(URLS.reports.skuOrdersSummary, GetOrdersReportSummaryRequest.toJSON(scope), {
      ignoreInternalError: true,
    });
    return transformKeysToCamelCase(GetOrdersReportSummaryResponse.fromJSON(response?.data || {}));
  } catch (error) {
    reportError(error);
    throw error;
  }
}

export function useOrdersReportSummaryQuery(scope) {
  return useQuery({
    queryKey: skuOrderReportsKeys.summary(scope),
    queryFn: () => fetchOrdersReportSummary(scope),
    enabled: Boolean(scope?.venue_ids?.length),
    select: normalizeOrdersReportSummary,
  });
}

async function fetchOrdersReport(filter, cursor) {
  try {
    const body = GetOrdersReportRequest.toJSON({
      ...filter,
      cursor_paging: { limit: ORDERS_REPORT_PAGE_SIZE, ...(cursor ? { cursor } : {}) },
    });
    const response = await httpClient.post(URLS.reports.skuOrders, body, { ignoreInternalError: true });
    return transformKeysToCamelCase(GetOrdersReportResponse.fromJSON(response?.data || {}));
  } catch (error) {
    reportError(error);
    throw error;
  }
}

const FIRST_PAGE = { page: 1, cursorByPage: { 1: null } };

export function useOrdersReportQuery(filter) {
  const [paging, setPaging] = useState({ filter, ...FIRST_PAGE });
  const { page, cursorByPage } = paging.filter === filter ? paging : FIRST_PAGE;
  const cursor = cursorByPage[page] ?? null;

  const query = useQuery({
    queryKey: skuOrderReportsKeys.orders(filter, cursor),
    queryFn: () => fetchOrdersReport(filter, cursor),
    enabled: Boolean(filter?.venue_ids?.length),
    select: normalizeOrdersReport,
  });

  const nextPageCursor = query.data?.nextPageCursor ?? null;

  const changePage = nextPage => {
    if (nextPage < 1 || (nextPage > page && !nextPageCursor)) return;
    setPaging({
      filter,
      page: nextPage,
      cursorByPage: nextPage > page ? { ...cursorByPage, [nextPage]: nextPageCursor } : cursorByPage,
    });
  };

  return { ...query, page, changePage, nextPageCursor };
}

export function useOrdersReportCsvExport(filter, { onDownloaded } = {}) {
  const dispatch = useDispatch();
  const [isExporting, setIsExporting] = useState(false);

  const exportCsv = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const response = await httpClient.post(URLS.reports.skuOrdersCsv, GetOrdersReportRequest.toJSON(filter), {
        ignoreInternalError: true,
        responseType: 'blob',
      });
      downloadCsvResponse(response, CSV_DEFAULT_FILE_NAME);
      dispatch(createSuccessSnackbar(messages.csvExportStarted));
      if (onDownloaded) {
        try {
          onDownloaded(await csvRowCount(new Blob([response.data])).catch(() => null));
        } catch (trackingError) {
          reportError(trackingError);
        }
      }
    } catch (error) {
      reportError(error);
      dispatch(createErrorSnackbar(messages.csvExportFailed));
    } finally {
      setIsExporting(false);
    }
  };

  return { exportCsv, isExporting };
}
